/**
 * Cross-Control Compliance Dashboard
 * Shows regulation coverage heatmap, compliance scores, and trend analysis
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Spin, Statistic, Progress, Table, Tag, Select, Alert } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  TrophyOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { listControls, getControlResults } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import { showError } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;

function ComplianceDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Compliance', path: '/trustgrid/compliance' }];
  }, [location.state]);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useState([]);
  const [results, setResults] = useState({});
  const [complianceData, setComplianceData] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('all');

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [controlsRes, resultsRes] = await Promise.all([
        listControls(),
        getControlResults(projectId).catch(() => ({ data: { results: [] } }))
      ]);

      const controlsData = controlsRes.data.controls || [];
      const resultsMap = {};
      (resultsRes.data.results || []).forEach(r => {
        resultsMap[r.control_code] = r;
      });

      const enriched = controlsData.map(c => ({
        ...c,
        status: resultsMap[c.code]?.status || 'unknown',
        confidence: resultsMap[c.code]?.confidence || 0,
        regulations: extractRegulations(c.regulation_data)
      }));

      setControls(enriched);
      setResults(resultsMap);
      calculateComplianceData(enriched);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'ComplianceDashboard');
      if (!wasHandled) {
        console.error('Failed to fetch data:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const extractRegulations = (regulationData) => {
    if (!regulationData || !regulationData.regulations) return [];
    const frameworks = new Set();
    regulationData.regulations.forEach(reg => {
      if (reg.framework) {
        frameworks.add(reg.framework);
      }
    });
    return Array.from(frameworks);
  };

  const calculateComplianceData = (controlsData) => {
    // Filter to only controls with actual scan results (excludes unknown/not-scanned)
    const scannedControls = controlsData.filter(c => c.status !== 'unknown');
    const total = scannedControls.length;
    const passed = scannedControls.filter(c => c.status === 'pass').length;
    const failed = scannedControls.filter(c => c.status === 'fail').length;
    const unknown = controlsData.filter(c => c.status === 'unknown').length;
    const warning = scannedControls.filter(c => c.status === 'warning').length;

    const overallScore = total > 0 ? Math.round((passed / total) * 100) : 0;

    // Dynamically extract all unique frameworks from scanned controls
    const allFrameworks = new Set();
    scannedControls.forEach(c => {
      if (c.regulations && Array.isArray(c.regulations)) {
        c.regulations.forEach(framework => allFrameworks.add(framework));
      }
    });

    // Calculate per-regulation coverage using only scanned controls
    const regulationCoverage = {};
    Array.from(allFrameworks).forEach(framework => {
      const frameworkControls = scannedControls.filter(c => c.regulations.includes(framework));
      const frameworkPassed = frameworkControls.filter(c => c.status === 'pass').length;
      const frameworkTotal = frameworkControls.length;

      regulationCoverage[framework] = {
        total: frameworkTotal,
        passed: frameworkPassed,
        failed: frameworkControls.filter(c => c.status === 'fail').length,
        score: frameworkTotal > 0 ? Math.round((frameworkPassed / frameworkTotal) * 100) : 0
      };
    });

    // Severity distribution (only scanned controls)
    const criticalControls = scannedControls.filter(c => c.severity === 'critical');
    const highControls = scannedControls.filter(c => c.severity === 'high');
    const mediumControls = scannedControls.filter(c => c.severity === 'medium');

    setComplianceData({
      total,
      passed,
      failed,
      unknown,
      warning,
      overallScore,
      regulationCoverage,
      severityData: {
        critical: {
          total: criticalControls.length,
          passed: criticalControls.filter(c => c.status === 'pass').length,
          score: criticalControls.length > 0
            ? Math.round((criticalControls.filter(c => c.status === 'pass').length / criticalControls.length) * 100)
            : 0
        },
        high: {
          total: highControls.length,
          passed: highControls.filter(c => c.status === 'pass').length,
          score: highControls.length > 0
            ? Math.round((highControls.filter(c => c.status === 'pass').length / highControls.length) * 100)
            : 0
        },
        medium: {
          total: mediumControls.length,
          passed: mediumControls.filter(c => c.status === 'pass').length,
          score: mediumControls.length > 0
            ? Math.round((mediumControls.filter(c => c.status === 'pass').length / mediumControls.length) * 100)
            : 0
        }
      }
    });
  };

  if (loading || !complianceData) {
    return (
      <TrustGridPage
        title="Compliance Dashboard"
        subtitle="Regulation coverage heatmap, compliance scores, and trend analysis"
        breadcrumbs={breadcrumbs}
        showRefresh
        onRefresh={fetchData}
      >
        <ProjectSelector />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </TrustGridPage>
    );
  }

  const filteredRegulationData = selectedFramework === 'all'
    ? Object.entries(complianceData.regulationCoverage)
    : [[selectedFramework, complianceData.regulationCoverage[selectedFramework]]];

  const getScoreColor = (score) => {
    if (score >= 90) return '#52c41a';
    if (score >= 70) return '#faad14';
    return '#f5222d';
  };

  const getScoreStatus = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'normal';
    return 'exception';
  };

  const regulationColumns = [
    {
      title: 'Framework',
      dataIndex: 'framework',
      key: 'framework',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Controls',
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'center'
    },
    {
      title: 'Passed',
      dataIndex: 'passed',
      key: 'passed',
      width: 100,
      align: 'center',
      render: (value) => <Tag color="green">{value}</Tag>
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      width: 100,
      align: 'center',
      render: (value) => value > 0 ? <Tag color="red">{value}</Tag> : <Tag color="default">0</Tag>
    },
    {
      title: 'Compliance Score',
      dataIndex: 'score',
      key: 'score',
      width: 200,
      render: (score) => (
        <Progress
          percent={score}
          strokeColor={getScoreColor(score)}
          status={getScoreStatus(score)}
        />
      )
    }
  ];

  const regulationTableData = filteredRegulationData.map(([framework, data]) => ({
    key: framework,
    framework,
    ...data
  }));

  return (
    <TrustGridPage
      title="Compliance Dashboard"
      subtitle="Regulation coverage heatmap, compliance scores, and trend analysis"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchData}
    >
      <ProjectSelector />

      {/* Overall Compliance Score */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Overall Compliance"
              value={complianceData.overallScore}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: getScoreColor(complianceData.overallScore) }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Passed Controls"
              value={complianceData.passed}
              suffix={`/ ${complianceData.total}`}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Failed Controls"
              value={complianceData.failed}
              suffix={`/ ${complianceData.total}`}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Warnings"
              value={complianceData.warning}
              suffix={`/ ${complianceData.total}`}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Severity Breakdown */}
      <Card title="Compliance by Severity" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <h4>Critical</h4>
              <Progress
                type="dashboard"
                percent={complianceData.severityData.critical.score}
                strokeColor={getScoreColor(complianceData.severityData.critical.score)}
                format={(percent) => `${percent}%`}
              />
              <div style={{ marginTop: '8px', color: '#888' }}>
                {complianceData.severityData.critical.passed} / {complianceData.severityData.critical.total} passed
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <h4>High</h4>
              <Progress
                type="dashboard"
                percent={complianceData.severityData.high.score}
                strokeColor={getScoreColor(complianceData.severityData.high.score)}
                format={(percent) => `${percent}%`}
              />
              <div style={{ marginTop: '8px', color: '#888' }}>
                {complianceData.severityData.high.passed} / {complianceData.severityData.high.total} passed
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <h4>Medium</h4>
              <Progress
                type="dashboard"
                percent={complianceData.severityData.medium.score}
                strokeColor={getScoreColor(complianceData.severityData.medium.score)}
                format={(percent) => `${percent}%`}
              />
              <div style={{ marginTop: '8px', color: '#888' }}>
                {complianceData.severityData.medium.passed} / {complianceData.severityData.medium.total} passed
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Regulation Coverage Heatmap */}
      <Card
        title="Regulation Framework Coverage"
        extra={
          <Select
            value={selectedFramework}
            onChange={setSelectedFramework}
            style={{ width: 200 }}
          >
            <Option value="all">All Frameworks</Option>
            {Object.keys(complianceData.regulationCoverage).sort().map(framework => (
              <Option key={framework} value={framework}>{framework}</Option>
            ))}
          </Select>
        }
        style={{ marginBottom: '24px' }}
      >
        <Table
          dataSource={regulationTableData}
          columns={regulationColumns}
          pagination={false}
          bordered
        />

        {complianceData.overallScore < 70 && (
          <Alert
            type="warning"
            message="Compliance Score Below Target"
            description="Your overall compliance score is below 70%. Review failed controls and take corrective action."
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>
    </TrustGridPage>
  );
}

export default ComplianceDashboard;
